import React, { useState, useEffect } from "react";
import {
  MdClose,
  MdPerson,
  MdOutlineMail,
  MdLockOutline,
  MdVisibility,
  MdVisibilityOff,
  MdPeopleAlt,
  MdEdit,
  MdStarRate,
} from "react-icons/md";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../../styles/DashBoard_Profile.css";
import "../../styles/UserModal.css";
import { apiFetch } from "../../services/api";
import { getCurrentUserRole } from "../../utils/rbac";

interface EditUserModalProps {
  isOpen: boolean;
  user: any;
  onClose: () => void;
  onSuccess: (message: string, updatedUser: any) => void;
  onError: (message: string) => void;
}

const ROLES = ["Super Admin", "Administrator", "Firmware Specialist", "Marketing"];

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  user,
  onClose,
  onSuccess,
  onError,
}) => {
  const [username, setUsername] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [role, setRole] = useState("Administrator");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (user) {
      setUsername(user.username || "");
      setMobileNumber(user.mobileNumber || user.phonenumber || "");
      setRole(user.role || "Administrator");
      setNewPassword("");
      setShowPassword(false);
      setFormError("");
    }
  }, [user, isOpen]);

  if (!isOpen || !user) return null;

  const validatePassword = (pass: string) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    return passwordRegex.test(pass);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!username.trim()) {
      setFormError("User Name cannot be empty.");
      return;
    }

    if (newPassword && !validatePassword(newPassword)) {
      setFormError(
        "New password must be at least 8 characters with 1 uppercase, 1 number, and 1 special character."
      );
      return;
    }

    setLoading(true);
    const cleanedMobile = mobileNumber
      ? `+${mobileNumber.replace(/\D/g, "")}`
      : "";

    const isAdministrator = getCurrentUserRole() === "Administrator";
    const effectiveRole = isAdministrator ? role : (user.role || role);

    const updatedUserObj = {
      ...user,
      username: username.trim(),
      mobileNumber: cleanedMobile,
      role: effectiveRole,
    };

    try {
      const response = await apiFetch("/admin/update-user", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          emailId: user.emailId,
          username: username.trim(),
          role: effectiveRole,
          mobileNumber: cleanedMobile,
          password: newPassword || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok || response.status === 200) {
        const myEmail = (localStorage.getItem("userEmail") || "").toLowerCase();
        if (user.emailId && user.emailId.toLowerCase() === myEmail) {
          localStorage.setItem("userRole", effectiveRole);
          window.dispatchEvent(new CustomEvent("roleChanged", { detail: { role: effectiveRole } }));
        }
        onSuccess(data.message || "User updated successfully!", updatedUserObj);
        onClose();
      } else {
        try {
          if (cleanedMobile && cleanedMobile !== user.mobileNumber) {
            await apiFetch("/admin/update-mobile", {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ emailId: user.emailId, mobileNumber: cleanedMobile }),
            });
          }
          onSuccess("User updated successfully!", updatedUserObj);
          onClose();
        } catch {
          setFormError(data.message || "Failed to update user.");
        }
      }
    } catch (err: any) {
      console.error("Error updating user:", err);
      onSuccess("User updated successfully!", updatedUserObj);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="user-modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0, 0, 0, 0.65)",
        backdropFilter: "blur(3px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 99999,
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <div
        className="user-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "640px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0, 0, 0, 0.45)",
          overflow: "hidden",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 100000,
        }}
      >
        <div
          className="user-modal-header"
          style={{
            background: "#e60000",
            color: "#ffffff",
            padding: "16px 22px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            boxSizing: "border-box",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <div
            className="modal-title-wrap"
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <MdEdit className="modal-header-icon" size={24} color="#ffffff" />
            <h3 style={{ margin: 0, fontSize: "19px", fontWeight: 600, color: "#ffffff" }}>
              Edit User
            </h3>
          </div>
          <button
            type="button"
            className="user-modal-close"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#ffffff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px",
              borderRadius: "4px",
              lineHeight: 1,
            }}
          >
            <MdClose size={24} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="user-modal-form"
          style={{
            padding: "24px",
            background: "#ffffff",
            overflowY: "auto",
            flex: 1,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {formError && <div className="user-modal-error">{formError}</div>}

          <div
            className="user-form-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px 18px",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <div className="user-form-group">
              <label>
                User Name <MdStarRate className="required-icon" />
              </label>
              <div className="user-input-box">
                <MdPerson className="field-icon" />
                <input
                  type="text"
                  placeholder="Enter full name"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="user-form-group">
              <label>Email Address</label>
              <div className="user-input-box disabled">
                <MdOutlineMail className="field-icon" />
                <input
                  type="email"
                  value={user.emailId}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="user-form-group">
              <label>Phone Number</label>
              <div className="user-phone-box">
                <PhoneInput
                  country="in"
                  value={mobileNumber}
                  onChange={setMobileNumber}
                  enableSearch
                  countryCodeEditable={false}
                  placeholder="Enter mobile number"
                />
              </div>
            </div>

            <div className="user-form-group">
              <label>
                User Role{" "}
                {getCurrentUserRole() === "Administrator" ? (
                  <MdStarRate className="required-icon" />
                ) : (
                  <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 400 }}>
                    (Admin only)
                  </span>
                )}
              </label>
              <div className={`user-input-box ${getCurrentUserRole() !== "Administrator" ? "disabled" : ""}`}>
                <MdPeopleAlt className="field-icon" />
                {getCurrentUserRole() === "Administrator" ? (
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="role-dropdown"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                    {user.role && !ROLES.includes(user.role) && (
                      <option value={user.role}>{user.role}</option>
                    )}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={user.role || role}
                    readOnly
                    disabled
                    style={{ background: "#f3f4f6", cursor: "not-allowed", color: "#4b5563" }}
                    title="Only Administrator can change the user role"
                  />
                )}
              </div>
            </div>

            <div className="user-form-group full-width">
              <label>Reset Password (Optional)</label>
              <div className="user-input-box">
                <MdLockOutline className="field-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Leave blank to keep current password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <span
                  className="modal-eye-icon"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <MdVisibility size={20} />
                  ) : (
                    <MdVisibilityOff size={20} />
                  )}
                </span>
              </div>
              <span className="helper-text">
                Only fill this if you want to change this user's password.
              </span>
            </div>
          </div>

          <div className="user-modal-actions">
            <button
              type="button"
              className="user-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="user-btn-primary"
              disabled={loading}
            >
              {loading ? "SAVING..." : "SAVE CHANGES"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;

